import { useEffect, useState } from "react";
import { LocalStorage } from "@swiggy-private/core-ui";
import { logMROEventsIntoNR } from "@cart-client/helpers/MROFoodCart";
import { EVENT_SRC_TYPE } from "@cart-client/constants/MROConstants";

const MRO_CART_TAB_COUNTER = "mro_cart_tab_counter";

/**
 * Persisted counter shape (stored in localStorage):
 *
 * epochAtWindowStart  — epoch ms when the current rolling window began.
 *                       Used to detect when resetWindowMs has elapsed and
 *                       the per-window count should reset.
 *
 * countInCurrentWindow — number of times FTUE was shown inside the current
 *                        rolling window. Resets to 0 when the window expires.
 *                        Capped at counterMaxCount (e.g. 2 times per 7 days).
 *
 * lifetimeCount        — total times FTUE has ever been shown across all
 *                        windows. Never resets. Capped at lifetimeMaxCount (e.g. 5).
 */
type FtueCounter = {
    epochAtWindowStart: number;
    countInCurrentWindow: number;
    lifetimeCount: number;
};

interface MROTabsFrequencyCappingConfig {
    counterMaxCount: number;
    counterResetInDays: number;
    lifetimeMaxCount: number;
}

export const useMROTabsFrequencyCapping = (config: MROTabsFrequencyCappingConfig | undefined): boolean => {
    const [showFtue, setShowFtue] = useState(false);

    useEffect(() => {
        if (!config) return;

        const { counterMaxCount, counterResetInDays, lifetimeMaxCount } = config;

        const ONE_DAY_MS = 24 * 60 * 60 * 1000;
        const currentEpochMs = Date.now();
        const resetWindowMs = counterResetInDays * ONE_DAY_MS;

        let persisted: FtueCounter | null = null;
        try {
            persisted = LocalStorage.getItem(MRO_CART_TAB_COUNTER) as FtueCounter | null;
        } catch (e) {
            logMROEventsIntoNR({ event: "mro_tabs_ftue_counter_read_error", error: String(e) });
        }

        const persistCounter = (counter: FtueCounter) => {
            try {
                LocalStorage.setItem(MRO_CART_TAB_COUNTER, counter);
            } catch (e) {
                logMROEventsIntoNR({ event: EVENT_SRC_TYPE.MRO_TABS_FTUE_COUNTER_WRITE_ERROR, error: String(e) });
            }
        };

        // initialise on first visit, or migrate gracefully from any old counter shape
        let ftueCounter: FtueCounter = persisted?.epochAtWindowStart
            ? persisted
            : {
                  epochAtWindowStart: currentEpochMs,
                  countInCurrentWindow: 0,
                  lifetimeCount: persisted?.lifetimeCount ?? 0,
              };

        // check if the rolling window (e.g. 7 days) has elapsed since it started
        const isWindowExpired = currentEpochMs - ftueCounter.epochAtWindowStart >= resetWindowMs;
        if (isWindowExpired) {
            // start a fresh window — reset per-window count but keep lifetime count intact
            ftueCounter = {
                ...ftueCounter,
                epochAtWindowStart: currentEpochMs,
                countInCurrentWindow: 0,
            };
        }

        // show FTUE only when both caps are satisfied:
        //   - countInCurrentWindow < counterMaxCount  → "x times in y days" rule
        //   - lifetimeCount < lifetimeMaxCount         → overall lifetime cap
        const canShowFtue =
            ftueCounter.lifetimeCount < lifetimeMaxCount && ftueCounter.countInCurrentWindow < counterMaxCount;

        if (!canShowFtue) {
            setShowFtue(false);
            persistCounter(ftueCounter);
            return;
        }

        // FTUE will be shown — increment both counters and persist
        setShowFtue(true);
        ftueCounter = {
            ...ftueCounter,
            countInCurrentWindow: ftueCounter.countInCurrentWindow + 1,
            lifetimeCount: ftueCounter.lifetimeCount + 1,
        };
        persistCounter(ftueCounter);
    }, [config]);

    return showFtue;
};
